import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { KeyRound, Lock, Unlock, EyeOff, Eye, Save, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  admin: "Admin", owner: "Owner", manager: "Manager",
  branch_manager: "Branch Manager", dispatcher: "Dispatcher",
  vendor: "Technician",
};

export default function SpecialPermissions() {
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterRole, setFilterRole] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [draftPerms, setDraftPerms] = useState(null);  // null=full | [...]=subset
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, us] = await Promise.all([
        API.get("/management/nav-permissions/catalog"),
        API.get("/management/users?limit=200"),
      ]);
      setCatalog(cat.data.items);
      setUsers(us.data.users);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredUsers = users.filter(u =>
    filterRole === "all" ? true : u.role === filterRole
  );

  const groupedCatalog = catalog.reduce((acc, it) => {
    (acc[it.app] = acc[it.app] || []).push(it);
    return acc;
  }, {});

  const openEditor = (u) => {
    if (u.role === "owner") {
      toast.info("Owner role always has full access — cannot be restricted.");
      return;
    }
    setEditingUser(u);
    setDraftPerms(u.nav_permissions ?? null);
  };

  const togglePerm = (key) => {
    setDraftPerms((cur) => {
      const base = cur ?? catalog.map(c => c.key);  // promote 'full' to explicit
      if (base.includes(key)) return base.filter(k => k !== key);
      return [...base, key];
    });
  };

  const resetToFull = () => setDraftPerms(null);
  const denyAll = () => setDraftPerms([]);

  const save = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await API.patch(`/management/users/${editingUser.id}/permissions`,
                      { nav_permissions: draftPerms });
      toast.success(`Permissions updated for ${editingUser.email}`);
      setEditingUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const permLabel = (u) => {
    if (u.role === "owner") return { label: "Full access (owner)", color: "bg-zinc-900 text-white" };
    if (u.nav_permissions == null) return { label: "Full access", color: "bg-emerald-100 text-emerald-700" };
    if (u.nav_permissions.length === 0) return { label: "No access", color: "bg-red-100 text-red-700" };
    return { label: `${u.nav_permissions.length} item(s) visible`, color: "bg-amber-100 text-amber-700" };
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="special-permissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-red-600" /> Special Permissions
          </h1>
          <p className="text-sm text-zinc-500">
            Hide navigation items for individual users. Restricted items are <strong>completely invisible</strong> — not greyed out.
          </p>
        </div>
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="p-4 flex items-center gap-2">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-48" data-testid="perm-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {Object.entries(ROLE_LABEL).map(([k, v]) =>
                <SelectItem key={k} value={k}>{v}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500">{filteredUsers.length} user(s)</p>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-3">
        {filteredUsers.map(u => {
          const meta = permLabel(u);
          return (
            <Card key={u.id} className="border border-zinc-200" data-testid={`perm-user-${u.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-900 truncate">{u.email}</p>
                  <p className="text-[10px] text-zinc-500">{ROLE_LABEL[u.role] || u.role}</p>
                  <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
                <Button variant="outline" size="sm"
                        onClick={() => openEditor(u)}
                        disabled={u.role === "owner"}
                        className="text-xs"
                        data-testid={`perm-edit-${u.id}`}>
                  <KeyRound className="w-3.5 h-3.5 mr-1" />
                  {u.role === "owner" ? "Locked" : "Edit"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-red-600" /> Permissions — {editingUser?.email}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Toggle individual navigation items. Unchecked items are completely hidden for this user.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  Unchecked items will be <strong>completely hidden</strong> — sidebar, sub-menus, dashboard widgets, and any links — in <strong>FieldProIQ AND ARM</strong> for this user. They will not appear at all.
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={resetToFull}
                        data-testid="perm-reset-full"
                        className="text-xs">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to full access
                </Button>
                <Button variant="outline" size="sm" onClick={denyAll}
                        data-testid="perm-deny-all"
                        className="text-xs text-red-600 border-red-200">
                  <EyeOff className="w-3.5 h-3.5 mr-1" /> Deny all
                </Button>
                <div className="ml-auto text-xs text-zinc-500 self-center">
                  {draftPerms == null ? "Full access (default)" : `${draftPerms.length} selected`}
                </div>
              </div>

              {Object.entries(groupedCatalog).map(([app, items]) => (
                <div key={app} className="border border-zinc-200 rounded-lg p-3">
                  <p className="text-xs font-bold uppercase text-zinc-500 mb-2">
                    {app === "management" ? "Management Portal"
                      : app === "vendor" ? "FieldProIQ (Technician)"
                      : "ARM (Homeowner)"}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {items.map(it => {
                      const isChecked = draftPerms == null ? true : draftPerms.includes(it.key);
                      return (
                        <label key={it.key}
                               className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-xs ${
                                 isChecked
                                   ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                   : "bg-zinc-50 border-zinc-200 text-zinc-500"
                               }`}
                               data-testid={`perm-toggle-${it.key}`}>
                          <input type="checkbox" checked={isChecked}
                                 onChange={() => togglePerm(it.key)}
                                 className="accent-red-600" />
                          {isChecked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          <span className="flex-1">{it.label}</span>
                          <code className="text-[9px] text-zinc-400">{it.key}</code>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="flex gap-2 sticky bottom-0 bg-white pt-2 border-t border-zinc-200">
                <Button variant="outline" className="flex-1" onClick={() => setEditingUser(null)}>
                  Cancel
                </Button>
                <Button onClick={save} disabled={saving}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="perm-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <><Save className="w-4 h-4 mr-1.5" /> Save Permissions</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
